import { useEffect, useState } from 'react';
import type { HomePopupConfigDTO, HomePopupUpdateParams, MarketConfigDTO } from '../../features/settings/types/settings.types';
import { formatDateTime } from '../../lib/format';
import { normalizeImageValueForSubmit, uploadImageToCos, resolveImagePreviewUrl } from '../../lib/upload/cos';

const emptyForm: HomePopupUpdateParams = {
  enabled: false,
  market_id: 'CN',
  title: '',
  content: '',
  image_url: '',
  button_text: '我知道了',
};

interface Props {
  data: HomePopupConfigDTO | null;
  saving: boolean;
  markets: MarketConfigDTO[];
  onUpdate: (params: HomePopupUpdateParams) => Promise<boolean>;
}

function pickPopupForm(data: HomePopupConfigDTO | null, marketId: string): HomePopupUpdateParams {
  const item = data?.items?.find((entry) => (entry.market_id || 'CN') === marketId);
  if (item) {
    return {
      enabled: item.enabled,
      market_id: item.market_id || marketId,
      title: item.title ?? '',
      content: item.content ?? '',
      image_url: item.image_url ?? '',
      button_text: item.button_text || '我知道了',
    };
  }
  if (data && (data.market_id || 'CN') === marketId) {
    return {
      enabled: data.enabled,
      market_id: data.market_id || marketId,
      title: data.title ?? '',
      content: data.content ?? '',
      image_url: data.image_url ?? '',
      button_text: data.button_text || '我知道了',
    };
  }
  return { ...emptyForm, market_id: marketId };
}

export function HomePopupSection({ data, saving, markets, onUpdate }: Props) {
  const defaultMarketId = markets[0]?.market_id || 'CN';
  const [selectedMarketId, setSelectedMarketId] = useState(defaultMarketId);
  const [form, setForm] = useState<HomePopupUpdateParams>(emptyForm);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (markets.length === 0) return;
    if (!markets.some((market) => market.market_id === selectedMarketId)) {
      setSelectedMarketId(defaultMarketId);
    }
  }, [defaultMarketId, markets, selectedMarketId]);

  useEffect(() => {
    setForm(pickPopupForm(data, selectedMarketId));
    setUploadedImageUrl('');
  }, [data, selectedMarketId]);

  const handleMarketChange = (marketId: string) => {
    setSelectedMarketId(marketId);
    setForm(pickPopupForm(data, marketId));
    setUploadedImageUrl('');
  };

  const save = async () => {
    const ok = await onUpdate({
      ...form,
      market_id: selectedMarketId,
      image_url: normalizeImageValueForSubmit(form.image_url ?? ''),
    });
    if (!ok || !data) return;
  };

  const handleImageChange = async (file: File | null) => {
    if (!file) return;
    try {
      setUploading(true);
      const result = await uploadImageToCos(file, 'home-popup');
      setUploadedImageUrl(result.readUrl);
      setForm((current) => ({ ...current, image_url: result.relativePath }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">首页弹窗</h2>
          <p className="mt-1 text-sm text-gray-500">
            启用后移动端首页会展示一次弹窗，文字必填，图片可选。
          </p>
        </div>
        {data?.updated_at ? (
          <span className="text-xs text-gray-400">
            更新于 {formatDateTime(data.updated_at)}
          </span>
        ) : null}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4 max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            所属地区 <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedMarketId}
            onChange={(event) => handleMarketChange(event.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {markets.map((market) => (
              <option key={market.market_id} value={market.market_id}>
                {market.market_id} · {market.name}
              </option>
            ))}
          </select>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          启用弹窗
        </label>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <input
            type="text"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            placeholder="弹窗标题"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <input
            type="text"
            value={form.button_text}
            onChange={(event) => setForm((current) => ({ ...current, button_text: event.target.value }))}
            placeholder="按钮文字"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <textarea
          value={form.content}
          onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
          placeholder="弹窗文字内容"
          rows={4}
          className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        />

        <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
          <input
            type="text"
            value={form.image_url}
            onChange={(event) => setForm((current) => ({ ...current, image_url: event.target.value }))}
            placeholder="图片 URL 或上传后自动生成"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <label className="flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            上传图片
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => void handleImageChange(event.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        {uploading ? <p className="text-xs text-gray-400">图片上传中...</p> : null}

        {form.image_url ? (
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
            <img
              src={resolveImagePreviewUrl(form.image_url, form.image_url === data?.image_url ? undefined : { [form.image_url]: uploadedImageUrl })}
              alt="弹窗预览"
              className="max-h-64 w-full object-contain"
            />
          </div>
        ) : null}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            保存弹窗
          </button>
          {form.image_url ? (
            <button
              type="button"
              onClick={() => setForm((current) => ({ ...current, image_url: '' }))}
              className="h-9 px-3 text-sm text-gray-500 transition-colors hover:text-gray-700"
            >
              移除图片
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
